var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import "./wh-icon";
let WhPaginationElement = class WhPaginationElement extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Used in the ARIA tags for screen readers. This should describe the unit being paged. Examples: Page, Item
         * @example
         * ```html
         * <wh-pagination affectedComponentAriaLabel="Page" ...>
         * ```
         *
         * @example
         * ```html
         * <wh-pagination affectedComponentAriaLabel="Item" ...>
         * ```
         */
        // eslint-disable-next-line @typescript-eslint/no-inferrable-types, prettier/prettier
        this.affectedComponentAriaLabel = "Page";
        /**
         * The current item or page number. Example: The "x" in "x of y".
         * ```html
         * <wh-pagination current-page-number="1" ...>
         * ```
         */
        // eslint-disable-next-line prettier/prettier
        this.currentPageNumber = 1;
        /**
         * Available variants for the component.
         * @example
         * ```html
         * <wh-pagination variant="number-of" ...>
         * ```
         */
        this.variant = "number-of";
        this.loop = false;
    }
    ariaLabelCurrent() {
        return `Current ${this.affectedComponentAriaLabel}, ${this.affectedComponentAriaLabel} ${this.currentPageNumber}`;
    }
    ariaLabelGoto(page) {
        return `Go to ${this.affectedComponentAriaLabel} ${page}`;
    }
    changePageHandler(event, direction, gotoPage = 1) {
        event.preventDefault();
        event.stopPropagation();
        const newPagePosition = direction === "direct" ? gotoPage : this.newPagePosition(direction);
        const eventData = {
            direction,
            newPagePosition,
            totalPages: this.totalPages,
            previousPagePosition: this.currentPageNumber,
            type: "PaginationEvent",
        };
        this.dispatchEvent(new CustomEvent("paginate", {
            detail: eventData,
        }));
    }
    nextButtonClickHandler(event) {
        this.changePageHandler(event, "next");
    }
    get nextPage() {
        if (this.loop && this.currentPageNumber === this.totalPages) {
            return 1;
        }
        return Math.min(this.currentPageNumber + 1, this.totalPages);
    }
    newPagePosition(direction) {
        if (direction === "next") {
            return this.nextPage;
        }
        return this.previousPage;
    }
    pageButtonClickHandler(event) {
        const goto = event.currentTarget.dataset.gotoPage;
        this.changePageHandler(event, "direct", Number(goto));
    }
    previousButtonClickHandler(event) {
        this.changePageHandler(event, "previous");
    }
    get previousPage() {
        if (this.loop && this.currentPageNumber === 1) {
            return this.totalPages;
        }
        return Math.max(this.currentPageNumber - 1, 1);
    }
    isPreviousButtonDisabled() {
        if (this.loop) {
            return false;
        }
        return Boolean(this.currentPageNumber === 1);
    }
    isNextButtonDisabled() {
        if (this.loop) {
            return false;
        }
        return Boolean(this.currentPageNumber === this.totalPages);
    }
    renderVariantDotted() {
        const dots = [];
        for (let index = 1; index <= this.totalPages; index += 1) {
            const active = index === this.currentPageNumber;
            const icon = active ? "circle" : "circle-solid";
            const label = active
                ? this.ariaLabelCurrent()
                : this.ariaLabelGoto(index);
            dots.push(html `<button
          aria-label=${label}
          data-goto-page=${index}
          aria-current=${active}
          @click=${this.pageButtonClickHandler}
        >
          <wh-icon name=${icon} label=${label}></wh-icon>
        </button>`);
        }
        return html `<span part="dots">${dots}</span>`;
    }
    renderVariantNumberOf() {
        return html `
      <span part="prefix"><slot name="prefix">Part</slot></span>
      <span
        part="current-page"
        aria-label=${this.ariaLabelCurrent()}
        aria-current="true"
        >${this.currentPageNumber}</span
      >
      <span part="status-of"><slot name="separator">of</slot></span>
      <span part="number-of-pages">${this.totalPages}</span>
    `;
    }
    render() {
        const statusContent = this.variant === "number-of"
            ? this.renderVariantNumberOf()
            : this.renderVariantDotted();
        return html `
      <nav
        part="nav"
        role="navigation"
        aria-label="${this.affectedComponentAriaLabel} Pagination"
      >
        <ul part="list">
          <li part="previous-button-container">
            <button
              class="image-button"
              data-testid="pagination-previous-button"
              aria-label=${this.ariaLabelGoto(this.previousPage)}
              @click=${this.previousButtonClickHandler}
              .disabled=${this.isPreviousButtonDisabled()}
            >
              <wh-icon
                name="caret-left-circle"
                .label=${this.ariaLabelGoto(this.previousPage)}
              ></wh-icon>
            </button>
          </li>
          <li part="status">${statusContent}</li>
          <li part="next-button-container">
            <button
              class="image-button"
              data-testid="pagination-next-button"
              aria-label=${this.ariaLabelGoto(this.nextPage)}
              @click=${this.nextButtonClickHandler}
              .disabled=${this.isNextButtonDisabled()}
            >
              <wh-icon
                name="caret-right-circle"
                .label=${this.ariaLabelGoto(this.nextPage)}
              ></wh-icon>
            </button>
          </li>
        </ul>
      </nav>
    `;
    }
};
/* Stryker disable next-line StringLiteral */
WhPaginationElement.styles = css `
    :host {
      --wh-dimension-spacer-xxxs: 4px;
      --wh-dimension-spacer-xxs: 8px;
      --wh-dimension-spacer-xs: 12px;
      --wh-theme-colors-primary: #323264ff;
      --wh-typography-standard-fontSize: 16px;
      --wh-typography-standard-fontFamily: "Source Sans Pro", helvetica, arial;

      display: inline-block;
      font-family: var(--wh-typography-standard-fontFamily, San-Serif);
      font-size: var(--wh-typography-standard-fontSize);
    }

    nav > ul {
      align-items: center;
      display: flex;
      gap: var(--wh-dimension-spacer-xxs);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .image-button {
      align-items: center;
      appearance: none;
      background-color: transparent;
      border: 0;
      color: var(--wh-theme-colors-primary);
      cursor: pointer;
      display: flex;
      font-size: 2em;
      padding: 0;
    }

    .image-button:disabled {
      cursor: not-allowed;
      opacity: 0.3;
    }

    [part="status"] {
      color: var(--wh-theme-colors-primary);
    }

    [part="dots"] {
      display: flex;
      margin: auto var(--wh-dimension-spacer-xxxs);
    }

    [part="dots"] > button {
      align-items: center;
      appearance: none;
      background-color: transparent;
      border: 0;
      cursor: pointer;
      display: flex;
      justify-items: center;
      padding: var(--wh-dimension-spacer-xxs) var(--wh-dimension-spacer-xxxs);
    }

    [part="dots"] wh-icon {
      font-size: 0.8em;
    }
  `;
__decorate([
    property({ attribute: "affected-component-aria-label" })
], WhPaginationElement.prototype, "affectedComponentAriaLabel", void 0);
__decorate([
    property({ attribute: "current-page-number", type: Number })
], WhPaginationElement.prototype, "currentPageNumber", void 0);
__decorate([
    property({ attribute: "total-pages", type: Number })
], WhPaginationElement.prototype, "totalPages", void 0);
__decorate([
    property()
], WhPaginationElement.prototype, "variant", void 0);
__decorate([
    property()
], WhPaginationElement.prototype, "loop", void 0);
__decorate([
    query("[part='next-button-container'] > button")
], WhPaginationElement.prototype, "nextButton", void 0);
__decorate([
    queryAll("[part='dots'] > button")
], WhPaginationElement.prototype, "pageButtons", void 0);
__decorate([
    query("[part='previous-button-container'] > button")
], WhPaginationElement.prototype, "previousButton", void 0);
WhPaginationElement = __decorate([
    customElement("wh-pagination")
], WhPaginationElement);
export { WhPaginationElement };
